import { useState, useContext, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from "react";
import { NavBarContext, SearchContext } from '../../App';
import useFetchSearchResults, {
  SECONDARY_SEARCH_DEBOUNCE_MS,
} from '../../hooks/search/useFetchSearchResults';
import { useSearchURLState } from '../../hooks/useSearchURLState';

import { Link, useLocation, useNavigate } from "react-router-dom";
import classes from "./SearchPage.module.css"

import NavBar from "../../components/NavBar/NavBar";
import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import StudioProfile from "../../components/StudioProfile/StudioProfile"
// import Button from "../../components/HomePage/Button";
import BottomPageSearch from "../../components/HomePage/BottomPageSearch";
import Map from "../../components/MapComponent";
import Footer from "../../components/HomePage/Footer";
import BottomFooter from "../../components/BottomFooter";
import PhoneSearch from "../../components/HomePage/PhoneSearch";
import { SearchFilters } from "../../components/HomePage/SearchFilters";
import Pagination from "../../components/SearchPage/Pagination";
import SearchError from "../../components/SearchPage/SearchError";
import { storeRedirectPathTo } from '../../util/redirectUtils';
import { CarouselSearchPriorityProvider } from '../../context/CarouselSearchPriorityContext';
import { useSelector } from "react-redux";
import { filterPublicStudios } from '../../util/studioFilters';
import { getStudioLowestPrice } from '../../util/studioPricing';
import { extractStudioTagValues, studioHasTagValue } from '../../util/tagsHelper';
import { trackSearchPerformed } from '../../util/tracking';

const MOBILE_STUDIO_TYPE_OPTIONS = [
  { key: 'Alle', label: 'Alle' },
  { key: 'Foto & Video Studios', label: 'Foto & Video Studios' },
  { key: 'Musik & Aufnahme Studios', label: 'Musik & Aufnahme Studios' },
];

/** Filter by URL `activeOption` using API field `studioTypes` (e.g. Foto, Video, Musik), not tags. */
function studioMatchesMobileStudioTypeFilter(studio, activeOption) {
  if (!activeOption || activeOption === 'Alle') return true;
  const types = Array.isArray(studio?.studioTypes) ? studio.studioTypes : [];
  if (activeOption === 'Foto & Video Studios') {
    return types.some((t) => t === 'Foto' || t === 'Video');
  }
  if (activeOption === 'Musik & Aufnahme Studios') {
    return types.includes('Musik');
  }
  return true;
}

export const SearchPage = () => {
  
  const { urlState, updateURLState, resetFilters } = useSearchURLState();
  const { isAuthenticated } = useSelector((state) => state?.auth);

  const {
    searchFields,
    setSearchFields,
    options,
    setOptions,
    setActiveInput,
    setIsPhoneSearch,
    updateSearchField,
    filteredActivities,
    filteredLocations,
    onLocationSelect: handleLocationSelect,
    selectLocation
  } = useContext(SearchContext);

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location?.search);
  // const [fadeOut, setFadeOut] = useState(false);
  const [ isPhone, setIsPhone ] = useState(false);
  // const [activeOption, setActiveOption] = useState('Alle');
  const [isPhoneSearchOpen, setIsPhoneSearchOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const { showSignIn, showRegister, setShowSignIn, setShowRegister } = useContext(NavBarContext);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const tagsContainerRef = useRef(null);
  const [showArrows, setShowArrows] = useState(true);
  const secondOptionsRef = useRef(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [displayedStudios, setDisplayedStudios] = useState([]);
  const ITEMS_PER_PAGE = 12;
  const [buttonArray, setButtonArray] = useState([]);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [mapButtonPosition, setMapButtonPosition] = useState(80);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const lastSearchKeyRef = useRef(null);
  const loadingStartedRef = useRef(false);
  const hasReceivedDataRef = useRef(false);
  const completionTimerRef = useRef(null);
  const lastTrackedSearchSignatureRef = useRef(null);
  
  // const limitedStudiosCount = (screenWidth < 1467 && screenWidth > 932) ? 3 : 4; // OLD: limit studios for limited users based on screen width

  const handleGastgeberClick = () => {
    if(isAuthenticated){

      navigate('/studio-erstellen');
    } else {
      // Make sure OAuth redirect returns to studio creation
      storeRedirectPathTo('/studio-erstellen');
      
      setShowSignIn(false);
      setShowRegister(true);
    }
  };
  
  const {
    activity: urlActivity,
    location: urlLocation,
    guests,
    tags: selectedTags,
    page: currentPage,
    minPrice,
    maxPrice,
    groupSize,
    creatives,
    bookingOptions,
    activeOption: urlActiveOption,
    showMap: urlShowMap,
    locationPlaceId: urlLocationPlaceId
  } = urlState;

  // useEffect(() => {
  //   const handleScroll = () => {
  //     const footer = document.querySelector('.targetedFooter');
  //     if (footer) {
  //       const footerRect = footer.getBoundingClientRect();
  //       const viewportHeight = window.innerHeight;

  //       if (viewportHeight - footerRect.top <= 60) {
  //         setFadeOut(false);
  //       } else {
  //         setFadeOut(true);
  //       }
  //     }
  //   };

  //   window.addEventListener('scroll', handleScroll);

  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   };
  // }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location?.search]);

  const searchParams = useMemo(() => {
    const params = {
      location: urlLocation,
      locationPlaceId: urlLocationPlaceId,
      radius: 30,
      studioTag: urlActivity || null,
      selectedGuest: guests,
      priceRange: [minPrice, maxPrice],
      key: `${urlLocation}-${urlLocationPlaceId}-${urlActivity}-${guests}`
    };
    
    return params;
  }, [urlLocation, urlLocationPlaceId, urlActivity, guests, minPrice, maxPrice]);

  // Create a search key that uniquely identifies the search query
  const searchKey = useMemo(() => `${urlLocation}-${urlLocationPlaceId}-${urlActivity}-${guests}`, [urlLocation, urlLocationPlaceId, urlActivity, guests]);

  const hasPlaceOrActivitySearch = useMemo(() => {
    const loc = typeof urlLocation === 'string' ? urlLocation.trim() : '';
    const act = typeof urlActivity === 'string' ? urlActivity.trim() : '';
    return Boolean(loc || act);
  }, [urlLocation, urlActivity]);

  // Unfiltered search params for getting the full price range
  const unfilteredSearchParams = useMemo(() => {
    const params = {
      location: urlLocation,
      locationPlaceId: urlLocationPlaceId,
      radius: 30,
      studioTag: urlActivity || null,
      selectedGuest: guests,
      priceRange: [0, 99999], // Get all studios regardless of price
      key: `${urlLocation}-${urlLocationPlaceId}-${urlActivity}-unfiltered`
    };
    
    return params;
  }, [urlLocation, urlLocationPlaceId, urlActivity, guests]);

  /*
   * Search perf (same ideas as large listing sites): (1) one “primary” GraphQL query drives the grid;
   * (2) a second wide price-range query runs later so it does not compete with the first on the network;
   * (3) Search requests use network-only pages to avoid stale cache results; CloudFront preconnect still helps images.
   * Further wins later: slimmer list query (fewer fields), server pagination, optional blur placeholders.
   */
  const {
    studios: allStudios,
    loading: studiosLoading,
    error,
    isLimitedUser,
    totalStudiosCount,
    searchCenter,
  } = useFetchSearchResults(searchParams);

  const {
    studios: unfilteredStudios,
    loading: unfilteredLoading,
    error: unfilteredError,
  } = useFetchSearchResults(unfilteredSearchParams, {
    debounceMs: SECONDARY_SEARCH_DEBOUNCE_MS,
  });

  // Apply all filters to studios
  const filteredStudios = useMemo(() => {
    // First filter by status - only show ACTIVE studios (already done in hook, but ensure it's applied)
    let filtered = filterPublicStudios(allStudios || []);

    // Apply price filters
    filtered = filtered.filter(studio => {
      const studioPrice = getStudioLowestPrice(studio);
      return studioPrice !== null && studioPrice >= minPrice && studioPrice <= maxPrice;
    });

    // Apply group size filter
    if (groupSize) {
      filtered = filtered.filter(studio => studio.groupSize === groupSize);
    }

    // Apply creatives filter
    if (creatives && creatives.length > 0) {
      filtered = filtered.filter(studio => 
        creatives.some(creative => studio.creatives?.includes(creative))
      );
    }

    // Apply booking options filter
    if (bookingOptions && bookingOptions.length > 0) {
      filtered = filtered.filter(studio => 
        bookingOptions.some(option => studio.bookingOptions?.includes(option))
      );
    }

    // Apply active option filter (mobile/desktop studio kind toggles — uses studioTypes, not tags)
    if (urlActiveOption && urlActiveOption !== 'Alle') {
      filtered = filtered.filter((studio) =>
        studioMatchesMobileStudioTypeFilter(studio, urlActiveOption)
      );
    }

    // Apply tag filters
    if (selectedTags && selectedTags?.length > 0) {
      filtered = filtered.filter(studio => {
        return selectedTags?.some(selectedTag => 
          studioHasTagValue(studio, selectedTag)
        );
      });
    }
    return filtered;
  }, [allStudios, minPrice, maxPrice, groupSize, creatives, bookingOptions, urlActiveOption, selectedTags]);

  // Keep track of total filtered count before limiting
  const totalFilteredCount = filteredStudios.length;

  const trackedFilterCount = useMemo(() => {
    let count = 0;
    if (selectedTags?.length) count += selectedTags.length;
    if (creatives?.length) count += creatives.length;
    if (bookingOptions?.length) count += bookingOptions.length;
    if (groupSize) count += 1;
    if (urlActiveOption && urlActiveOption !== 'Alle') count += 1;
    if (minPrice > 0 || maxPrice < 99999) count += 1;
    return count;
  }, [selectedTags, creatives, bookingOptions, groupSize, urlActiveOption, minPrice, maxPrice]);

  useEffect(() => {
    if (!hasPlaceOrActivitySearch || studiosLoading || isInitializing || error) return;

    const signature = JSON.stringify({
      activity: urlActivity || '',
      location: urlLocation || '',
      guests: guests || '',
      selectedTags,
      minPrice,
      maxPrice,
      groupSize,
      creatives,
      bookingOptions,
      activeOption: urlActiveOption || 'Alle',
      totalFilteredCount,
    });

    if (lastTrackedSearchSignatureRef.current === signature) return;
    lastTrackedSearchSignatureRef.current = signature;

    trackSearchPerformed({
      activity: urlActivity,
      location: urlLocation,
      resultsCount: totalFilteredCount,
      filtersCount: trackedFilterCount,
      source: 'search_page',
    });
  }, [
    hasPlaceOrActivitySearch,
    studiosLoading,
    isInitializing,
    error,
    urlActivity,
    urlLocation,
    guests,
    selectedTags,
    minPrice,
    maxPrice,
    groupSize,
    creatives,
    bookingOptions,
    urlActiveOption,
    totalFilteredCount,
    trackedFilterCount,
  ]);

  // const totalPages = Math.ceil(limitedFilteredStudios.length / ITEMS_PER_PAGE); // OLD: used limited list
  const totalPages = Math.ceil(filteredStudios.length / ITEMS_PER_PAGE);
  
  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const pageStudios = filteredStudios.slice(startIndex, endIndex);
    
    setDisplayedStudios(pageStudios);

    // OLD: store only a limited set of studios for non-authenticated users
    // This was used together with a restriction in Studio.jsx to only allow opening these studios.
    // if (!isAuthenticated && pageStudios.length > 0) {
    //   const studioIds = pageStudios.map(studio => studio.id).filter(Boolean);
    //   if (studioIds.length > 0) {
    //     sessionStorage.setItem('allowedStudioIds', JSON.stringify(studioIds));
    //   }
    // } else if (isAuthenticated) {
    //   sessionStorage.removeItem('allowedStudioIds');
    // }
  }, [filteredStudios, currentPage, isAuthenticated]);

  // Handle page changes
  const handlePageChange = useCallback((page) => {
    updateURLState({ page });
  }, [updateURLState]);

  // Handle tag selection
  const handleTagSelect = useCallback((tagName) => {
    const newTags = selectedTags.includes(tagName)
      ? selectedTags.filter(tag => tag !== tagName)
      : [...selectedTags, tagName];
    
    updateURLState({ 
      tags: newTags 
    }, { 
      resetPage: true 
    });
  }, [selectedTags, updateURLState]);

  // Handle filter updates from SearchFilters component
  const handleFilterUpdate = useCallback((filterUpdates) => {
    updateURLState(filterUpdates, { resetPage: true });
  }, [updateURLState]);

  useEffect(() => {
    const checkContainerSizes = () => {
      if (tagsContainerRef.current && secondOptionsRef.current) {
        const tagsWidth = tagsContainerRef.current.scrollWidth;
        const containerWidth = secondOptionsRef.current.offsetWidth;
        
        setShowArrows(tagsWidth > containerWidth);
      }
    };

    checkContainerSizes();
    
    const timer = setTimeout(() => {
      checkContainerSizes();
    }, 100);
    
    window.addEventListener('resize', checkContainerSizes);
    
    return () => {
      window.removeEventListener('resize', checkContainerSizes);
      clearTimeout(timer);
    };
  }, [buttonArray]);

  const handleScroll = (direction) => {
    const container = tagsContainerRef.current;
    if (!container) return;

    const scrollAmount = container.offsetWidth * 0.8;
    const newPosition = direction === 'right' 
      ? scrollPosition + scrollAmount 
      : scrollPosition - scrollAmount;

    const maxScroll = container.scrollWidth - container.offsetWidth;

    const boundedPosition = Math.max(0, Math.min(newPosition, maxScroll));
    
    container.scrollTo({
      left: boundedPosition,
      behavior: 'smooth'
    });
    
    setScrollPosition(boundedPosition);
    
    setShowLeftArrow(boundedPosition > 0);
    setShowRightArrow(boundedPosition < maxScroll);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsPhone(window.innerWidth <= 742);
      setScreenHeight(window.innerHeight);
      setScreenWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (showFilters) {
      document.querySelector('.App').style.height = '100vh';
    } else {
      document.querySelector('.App').style.height = 'auto';
    }
  }, [showFilters]);
  
  const handlePhoneSearchOpen = () => {
    setSearchFields({
      activity: '',
      location: '',
      locationPlaceId: ''
    });
    setIsPhoneSearchOpen(true);
  };

  const handlePhoneSearchClose = () => {
    setIsPhoneSearchOpen(false);
  }

  const handleFiltersOpen = () => {
    setShowFilters(true);
  };

  // const handleFiltersClose = () => {
  //   setShowFilters(false);
  // };

  // Reset initialization tracking when search query changes
  useEffect(() => {
    if (lastSearchKeyRef.current === null || searchKey !== lastSearchKeyRef.current) {
      // Search query has changed (or initial mount), reset initialization state
      lastSearchKeyRef.current = searchKey;
      loadingStartedRef.current = false;
      hasReceivedDataRef.current = false;
      // Clear any pending completion timer
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
      setIsInitializing(true);
    }
  }, [searchKey]);

  // Track when loading starts
  useEffect(() => {
    if (studiosLoading && searchKey === lastSearchKeyRef.current) {
      loadingStartedRef.current = true;
      // Cancel any pending completion timer since we're loading again
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    }
  }, [studiosLoading, searchKey]);

  // Track when we receive data
  useEffect(() => {
    if (allStudios.length > 0 && searchKey === lastSearchKeyRef.current) {
      hasReceivedDataRef.current = true;
      // Cancel any pending completion timer since we have data
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
      // Mark initialization complete immediately when we receive data
      if (isInitializing) {
        setIsInitializing(false);
      }
    }
  }, [allStudios.length, searchKey, isInitializing]);

  // Track when initial load completes for the current search
  // Only mark as complete after loading has been continuously false for a delay
  useEffect(() => {
    if (searchKey !== lastSearchKeyRef.current) return;
    
    // Skip if we already have data or haven't started loading
    if (hasReceivedDataRef.current || !loadingStartedRef.current) return;
    
    // If loading has completed and we have no data, start a timer
    // The timer will be cancelled if loading starts again or data arrives
    if (!studiosLoading && !hasReceivedDataRef.current && isInitializing && allStudios.length === 0) {
      // Clear any existing timer
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
      }
      
      // Start a new timer to mark completion after delay
      completionTimerRef.current = setTimeout(() => {
        // Double-check conditions after delay
        if (
          !studiosLoading && 
          searchKey === lastSearchKeyRef.current && 
          !hasReceivedDataRef.current && 
          isInitializing &&
          allStudios.length === 0
        ) {
          // All queries have completed with no data - safe to show "no results"
          setIsInitializing(false);
        }
        completionTimerRef.current = null;
      }, 1000); // 1 second delay to ensure all sequential queries complete
    }
    
    // Cleanup on unmount or search key change
    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
  }, [studiosLoading, isInitializing, searchKey, allStudios.length]);

  const getTagsByPopularity = (studios) => {
    const tagCounts = {};
    
    // Go through all studios and count tag occurrences
    studios.forEach(studio => {
      extractStudioTagValues(studio).forEach(tagName => {
        tagCounts[tagName] = (tagCounts[tagName] || 0) + 1;
      });
    });
    
    // Convert to array and sort by count (descending)
    const sortedTags = Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);
    
    return sortedTags;
  };

  useEffect(() => {
    if (allStudios && allStudios.length > 0) {
      const tagsByPopularity = getTagsByPopularity(allStudios);
      
      // Create buttonArray from tags
      const dynamicButtonArray = tagsByPopularity.map((tagItem, index) => ({
        key: index + 1,
        text: tagItem.tag
      }));
      
      setButtonArray(dynamicButtonArray);
      
      // If you want just the tag names in order:
      const tagNamesOnly = tagsByPopularity.map(item => item.tag);
    }
  }, [allStudios]);

  const handleMapButtonClick = () => {
    updateURLState({ showMap: !urlShowMap });
  };

  const handleMobileStudioTypeChange = useCallback((option) => {
    updateURLState({ activeOption: option }, { resetPage: true });
  }, [updateURLState]);

  useLayoutEffect(() => {
    const calculateMapButtonPosition = () => {
      const mainContentElement = document.querySelector(`.${classes.mainContentContainer}`);
      
      if (!mainContentElement) return 75;
      
      const mainContentHeight = mainContentElement.offsetHeight;
      const navBarHeight = 70;
      
      // Calculate the bottom position: 80px above the bottom of mainContentContainer
      const buttonPositionFromTop = (navBarHeight + mainContentHeight) - 75;
      const bottomPosition = screenHeight - buttonPositionFromTop;
      
      return Math.max(75, bottomPosition);
    };

    // Only calculate after studios are loaded and displayed
    if (!studiosLoading && displayedStudios.length > 0) {
      const newPosition = calculateMapButtonPosition();
      setMapButtonPosition(newPosition);
    }
  }, [studiosLoading, displayedStudios, screenHeight, allStudios, filteredStudios]);

  return (
    <div 
      className={`${classes.container} ${showFilters || showSignIn || showRegister ? classes.width : ''}`}
    >
      <div className={classes.mainContentContainer}>
      <div className={classes.navBarContainer}>
        <NavBar 
          fromSearch={true} 
          isLoading={studiosLoading} 
          error={error} 
          searchResults={allStudios || []} 
        />
      </div>
      <div className={classes.navBarPhoneContainer}>
        <NavBarPhone 
          searchResults={allStudios || []} 
          fromSearch={true} 
          isLoading={studiosLoading} 
          error={error} 
          showSignIn={showSignIn} 
          showRegister={showRegister}
        />
      </div>
      <div className={classes.phoneContainer}>
        <Link to="/">
          <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
              <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
              <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
              <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
          </svg>
          </Link>
        <div className={classes.inputGroupWrapper}>
          <div className={classes.inputGroup}>
            <div className={classes.inputInnerContainer}>
              <div className={classes.searchIconContainer} style={{ margin: "1.05rem .687rem .85rem 1.235rem" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="19.5" height="19.5" viewBox="0 0 19.5 19.5" fill="none">
                  <path d="M14.5163 12.9148C15.5278 11.5786 16.0887 9.93102 16.0887 8.24324C16.0887 3.98358 12.6534 0.517578 8.43693 0.517578C4.22044 0.517578 0.785156 3.98358 0.785156 8.24324C0.785156 12.5029 4.21043 15.9588 8.43693 15.9588C10.1095 15.9588 11.742 15.4063 13.0741 14.3816L18.853 20.0176L20.2852 18.5408L14.5163 12.9148ZM8.6773 13.8993H8.43693C5.34217 13.8993 2.8283 11.3576 2.8283 8.23319C2.8283 5.10877 5.34217 2.58713 8.43693 2.58713C11.5317 2.58713 14.0355 5.11881 14.0355 8.24324C14.0355 11.3677 11.6819 13.7687 8.6773 13.8993Z" fill="#77808A" />           
                </svg>
              </div>
              <div className={classes.searchText}>
                {params?.get('activity') || params?.get('location')
                  ? 
                  <button 
                    className={classes.searchTextButton} 
                    onClick={handlePhoneSearchOpen}
                  >
                    <span className={classes.searchTextButtonActivity}>
                      {`${params?.get('activity') || ''}`}
                    </span>
                    <span className={classes.searchTextButtonLocation}>
                      {`${params?.get('location') || ''}`}
                    </span>
                  </button>
                  : 
                <input
                  className='regularLarge'
                  placeholder='Studio/Location suchen'
                  type="text"
                  onClick={handlePhoneSearchOpen}
                />
                }
              </div>
            </div>
            <button style={{ borderRadius: !params?.get('activity') && !params?.get('location') ? '100px' : '50%', width: !params?.get('activity') && !params?.get('location') ? 'auto' : '40px' }} disabled={studiosLoading} onClick={handleFiltersOpen} className={classes.filterButtonPhone}>
              {!params?.get('activity') && !params?.get('location') && <span style={{ fontWeight: '400' }} className="mediumLarge">Filter</span>}
              <div className={classes.box}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="10" viewBox="0 0 17 10" fill="none">
                <path d="M5.66667 9.20833C5.66667 9.02047 5.74129 8.8403 5.87413 8.70747C6.00697 8.57463 6.18714 8.5 6.375 8.5H10.625C10.8129 8.5 10.993 8.57463 11.1259 8.70747C11.2587 8.8403 11.3333 9.02047 11.3333 9.20833C11.3333 9.3962 11.2587 9.57636 11.1259 9.7092C10.993 9.84204 10.8129 9.91667 10.625 9.91667H6.375C6.18714 9.91667 6.00697 9.84204 5.87413 9.7092C5.74129 9.57636 5.66667 9.3962 5.66667 9.20833ZM2.83333 4.95833C2.83333 4.77047 2.90796 4.5903 3.0408 4.45747C3.17364 4.32463 3.3538 4.25 3.54167 4.25H13.4583C13.6462 4.25 13.8264 4.32463 13.9592 4.45747C14.092 4.5903 14.1667 4.77047 14.1667 4.95833C14.1667 5.1462 14.092 5.32636 13.9592 5.4592C13.8264 5.59204 13.6462 5.66667 13.4583 5.66667H3.54167C3.3538 5.66667 3.17364 5.59204 3.0408 5.4592C2.90796 5.32636 2.83333 5.1462 2.83333 4.95833ZM0 0.708333C0 0.520472 0.0746279 0.340304 0.207466 0.207466C0.340304 0.0746279 0.520472 0 0.708333 0H16.2917C16.4795 0 16.6597 0.0746279 16.7925 0.207466C16.9254 0.340304 17 0.520472 17 0.708333C17 0.896195 16.9254 1.07636 16.7925 1.2092C16.6597 1.34204 16.4795 1.41667 16.2917 1.41667H0.708333C0.520472 1.41667 0.340304 1.34204 0.207466 1.2092C0.0746279 1.07636 0 0.896195 0 0.708333Z" fill="black"/>
                </svg>
              </div>
            </button>
          </div>
          <div className={classes.mobileStudioTypeContainer}>
            {MOBILE_STUDIO_TYPE_OPTIONS.map((option) => {
              const isActive = (urlActiveOption || 'Alle') === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  className={`${classes.mobileStudioTypeButton} ${isActive ? classes.mobileStudioTypeButtonActive : ''}`}
                  onClick={() => handleMobileStudioTypeChange(option.key)}
                >
                  {option.key === 'Foto & Video Studios' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="12" viewBox="0 0 19 12" fill="none">
                      <path d="M9.88 0C11.559 0 12.92 1.34314 12.92 3V9C12.92 10.6569 11.559 12 9.88 12H3.04C1.36105 12 0 10.6569 0 9V3C0 1.34314 1.36105 0 3.04 0H9.88ZM14.44 7.74285V4.2573L17.0763 1.79442C17.803 1.11555 19 1.62391 19 2.61139V9.38865C19 10.3761 17.803 10.8845 17.0763 10.2056L14.44 7.74285Z" fill="#DC6A83"/>
                    </svg>
                  )}
                  {option.key === 'Musik & Aufnahme Studios' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="14" viewBox="0 0 10 14" fill="none">
                      <path d="M9.46429 6.3C9.7355 6.3 9.95964 6.49754 9.99514 6.75374L10 6.825V7.175C10 9.66658 8.03214 11.7078 5.53643 11.8872L5.53571 13.475C5.53571 13.7649 5.29586 14 5 14C4.72879 14 4.50464 13.8025 4.46914 13.5463L4.46429 13.475V11.8873C2.02374 11.7121 0.0877356 9.7566 0.00289985 7.34041L0 7.175V6.825C0 6.53506 0.23985 6.3 0.535714 6.3C0.806929 6.3 1.03106 6.49754 1.06654 6.75374L1.07143 6.825V7.175C1.07143 9.1539 2.66746 10.7675 4.66686 10.8469L4.82143 10.85H5.17857C7.19786 10.85 8.84436 9.28592 8.92543 7.32648L8.92857 7.175V6.825C8.92857 6.53506 9.16843 6.3 9.46429 6.3ZM5 0C6.57793 0 7.85714 1.2536 7.85714 2.8V7C7.85714 8.54637 6.57793 9.8 5 9.8C3.42204 9.8 2.14286 8.54637 2.14286 7V2.8C2.14286 1.2536 3.42204 0 5 0Z" fill="#4C39D8"/>
                    </svg>
                  )}
                  <span className={classes.mobileStudioTypeLabel}>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <SearchContext.Provider
          value={{
            searchFields,
            setSearchFields,
            options,
            setOptions,
            setActiveInput,
            setIsPhoneSearch,
            updateSearchField,
            filteredActivities,
            filteredLocations,
            onLocationSelect: handleLocationSelect,
            selectLocation
          }}
        >
          {isPhone && (
            <PhoneSearch close={handlePhoneSearchClose} isOpen={isPhoneSearchOpen} setIsPhoneSearchOpen={setIsPhoneSearchOpen}/>
          )}
        </SearchContext.Provider>
        <BottomPageSearch />
      </div>
      {hasPlaceOrActivitySearch &&
        (isInitializing || studiosLoading ? (
          <div className={classes.skeletonSearchedTitleOutsideContainer}>
            <div className={classes.skeletonSearchedTitleContainer}></div>
          </div>
        ) : (
          !error && (
            <div className={classes.searchedTitleContainer}>
              <h1 className="titleSemibold">
                {params?.get('activity')} {params?.get('activity') && params?.get('location') ? 'in' : ''} {params?.get('location')}
              </h1>
              <div className={classes.resultsCountContainer}>
                {!studiosLoading && (
                  <>
                    {/* OLD: show limited count for some users */}
                    {/* <p className="semiboldLarge">
                  {isLimitedUser ? totalFilteredCount : limitedFilteredStudios.length} Ergebnisse
                </p> */}
                    <p className="semiboldLarge">{filteredStudios.length} Ergebnisse</p>
                  </>
                )}
              </div>
            </div>
          )
        ))}
      {!hasPlaceOrActivitySearch && !error && !studiosLoading && (
        <div className={classes.resultsCountContainer}>
          <p className="semiboldLarge">{filteredStudios.length} Ergebnisse</p>
        </div>
      )}
      {!error && 
      <div className={classes.filterContainer}>
        <div className={classes.secondOptionsContainer} 
          ref={secondOptionsRef}
          style={{ justifyContent: !showLeftArrow && showArrows ? 'flex-end' : 'space-between' }}
        >
          <div className={classes.leftButtonContainer} 
                  style={{ display: showLeftArrow && showArrows ? 'flex' : 'none' }}
          >
            <button className={classes.leftButton} onClick={() => handleScroll('left')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none">
                <circle cx="19" cy="19" r="18.5" transform="rotate(180 19 19)" fill="white" stroke="#E2E8EF"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M14.0455 19.0019L21.4791 26.4354L22.4721 25.4424L15.0385 18.0088L14.0455 19.0019Z" fill="#1F1F1F"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M21.4798 10.9492L14.0462 19.0023L15.0781 19.9548L22.5117 11.9018L21.4798 10.9492Z" fill="#1F1F1F"/>
              </svg>
            </button>
          </div>
          {studiosLoading || isInitializing ? (
          <div className={classes.skeletonTagsContainer}>
          </div>
          ) : (
          // <div 
          //   style={{ position: showArrows ? 'absolute' : 'relative', width: showArrows ? '100%' : 'fit-content' }} 
          //   className={classes.tagsContainer} 
          //   ref={tagsContainerRef}
          // >  
          //     {buttonArray?.map(button => (
          //       <Button 
          //         key={button.key} 
          //         text={button.text}
          //         onClick={() => handleTagSelect(button.text)}
          //         activebutton={selectedTags?.includes(button.text)}
          //       />
          //     ))}
          // </div>
          <div className={classes.mobileStudioTypeContainer}>
            {MOBILE_STUDIO_TYPE_OPTIONS.map((option) => {
              const isActive = (urlActiveOption || 'Alle') === option.key;

              return (
                <button
                  key={option.key}
                  type="button"
                  className={`${classes.mobileStudioTypeButton} ${isActive ? classes.mobileStudioTypeButtonActive : ''}`}
                  onClick={() => handleMobileStudioTypeChange(option.key)}
                >
                  {option.key === 'Foto & Video Studios' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="12" viewBox="0 0 19 12" fill="none">
                      <path d="M9.88 0C11.559 0 12.92 1.34314 12.92 3V9C12.92 10.6569 11.559 12 9.88 12H3.04C1.36105 12 0 10.6569 0 9V3C0 1.34314 1.36105 0 3.04 0H9.88ZM14.44 7.74285V4.2573L17.0763 1.79442C17.803 1.11555 19 1.62391 19 2.61139V9.38865C19 10.3761 17.803 10.8845 17.0763 10.2056L14.44 7.74285Z" fill="#DC6A83"/>
                    </svg>
                  )}
                  {option.key === 'Musik & Aufnahme Studios' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="14" viewBox="0 0 10 14" fill="none">
                      <path d="M9.46429 6.3C9.7355 6.3 9.95964 6.49754 9.99514 6.75374L10 6.825V7.175C10 9.66658 8.03214 11.7078 5.53643 11.8872L5.53571 13.475C5.53571 13.7649 5.29586 14 5 14C4.72879 14 4.50464 13.8025 4.46914 13.5463L4.46429 13.475V11.8873C2.02374 11.7121 0.0877356 9.7566 0.00289985 7.34041L0 7.175V6.825C0 6.53506 0.23985 6.3 0.535714 6.3C0.806929 6.3 1.03106 6.49754 1.06654 6.75374L1.07143 6.825V7.175C1.07143 9.1539 2.66746 10.7675 4.66686 10.8469L4.82143 10.85H5.17857C7.19786 10.85 8.84436 9.28592 8.92543 7.32648L8.92857 7.175V6.825C8.92857 6.53506 9.16843 6.3 9.46429 6.3ZM5 0C6.57793 0 7.85714 1.2536 7.85714 2.8V7C7.85714 8.54637 6.57793 9.8 5 9.8C3.42204 9.8 2.14286 8.54637 2.14286 7V2.8C2.14286 1.2536 3.42204 0 5 0Z" fill="#4C39D8"/>
                    </svg>
                  )}
                  <span className={classes.mobileStudioTypeLabel}>{option.label}</span>
                </button>
              );
            })}
          </div>
          )}
          {/* <div className={classes.rightButtonContainer}
              style={{ display: showRightArrow && showArrows ? 'flex' : 'none' }}
          >
            <button className={classes.rightButton} onClick={() => handleScroll('right')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none">
                <circle cx="19" cy="19" r="18.5" transform="rotate(180 19 19)" fill="white" stroke="#E2E8EF"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M23.9545 19.0019L16.5209 26.4354L15.5279 25.4424L22.9615 18.0088L23.9545 19.0019Z" fill="#1F1F1F"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M16.5202 10.9492L23.9538 19.0023L22.9219 19.9548L15.4883 11.9018L16.5202 10.9492Z" fill="#1F1F1F"/>
              </svg>
            </button>
          </div> */}
        </div>
        <div className={classes.resultsCountContainer2}>
            {!studiosLoading && !isInitializing && (
              <>
                <span className="semiboldSmall">
                  Gezeigt
                </span>
                {/* OLD: show limited count for some users */}
                {/* <p className={`semiboldSmall ${classes.results}`}>
                  {isLimitedUser ? totalFilteredCount : limitedFilteredStudios.length} Ergebnisse
                </p> */}
                <p className={`semiboldSmall ${classes.results}`}>
                  {filteredStudios.length} Ergebnisse
                </p>
              </>
            )}
          </div>
        <div className={classes.smallLine}></div>
        {studiosLoading || isInitializing ? (
          // Loading skeleton for filter button
          <div className={classes.skeletonFilterButton}>

          </div>
        ) : (
          <button disabled={studiosLoading} onClick={handleFiltersOpen} className={classes.filterButton}>
            <span className="mediumLarge">Filter</span>
            <div className={classes.box}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M4.21364 1.76367C3.60585 1.76367 3.0917 2.28001 3.0917 2.94413C3.0917 3.60826 3.60585 4.1246 4.21364 4.1246C4.82142 4.1246 5.33557 3.60826 5.33557 2.94413C5.33557 2.28001 4.82142 1.76367 4.21364 1.76367ZM2.14938 2.43878C2.36906 1.47759 3.20155 0.75 4.21364 0.75C5.22577 0.75 6.05829 1.47766 6.27793 2.43891H16.9993V3.45257H6.27719C6.05643 4.41223 5.22464 5.13827 4.21364 5.13827C3.20258 5.13827 2.37076 4.41217 2.15005 3.45244H0V2.43878H2.14938ZM13.5715 7.81831C12.9637 7.81831 12.4495 8.33465 12.4495 8.99878C12.4495 9.66291 12.9637 10.1792 13.5715 10.1792C14.1792 10.1792 14.6934 9.66291 14.6934 8.99878C14.6934 8.33465 14.1792 7.81831 13.5715 7.81831ZM11.5071 8.49406C11.7265 7.53256 12.5591 6.80464 13.5715 6.80464C14.5838 6.80464 15.4164 7.53255 15.6359 8.49404H17V9.5077H15.6349C15.414 10.4671 14.5823 11.1929 13.5715 11.1929C12.5606 11.1929 11.729 10.4671 11.508 9.50773H0V8.49406H11.5071ZM7.31409 13.8754C6.7063 13.8754 6.19215 14.3917 6.19215 15.0559C6.19215 15.72 6.7063 16.2363 7.31409 16.2363C7.92187 16.2363 8.43602 15.72 8.43602 15.0559C8.43602 14.3917 7.92187 13.8754 7.31409 13.8754ZM5.2502 14.5489C5.47045 13.5885 6.30257 12.8617 7.31409 12.8617C8.32553 12.8617 9.15761 13.5884 9.37793 14.5487H16.9982V15.5623H9.37809C9.15801 16.523 8.32578 17.25 7.31409 17.25C6.30246 17.25 5.47027 16.5231 5.25013 15.5625H0V14.5489H5.2502Z" fill="#1F1F1F"/>
              </svg>
            </div>
          </button>
        )}
      </div>
      }
      {urlShowMap && !studiosLoading ? 
      (
        <div className={classes.mapContainer}>
          {/* OLD: pass only limited studios for some users */}
          {/* <Map studios={limitedFilteredStudios || []} /> */}
          <Map studios={filteredStudios || []} searchCenter={searchCenter} fromSearch />
            {displayedStudios?.length > 0 && (
              <button
                className={`${urlShowMap ? classes.mapClose : classes.mapOpen} regularSmall`}
                onClick={handleMapButtonClick}

              >
                {urlShowMap ? 'Liste anzeigen' : 'Karte anzeigen'}
              </button>
            )}
        </div>
      ) : (
      <div 
          className={`${classes.studiosContainer} ${isLimitedUser ? classes.limitedUser : ''}`} 
          style={{ justifyContent: allStudios.length !== 0 || studiosLoading ? 'flex-start' : 'center' }}
        >
         {studiosLoading || isInitializing ? (
           Array?.from({ length: ITEMS_PER_PAGE })?.map((_, index) => (
             <StudioProfile key={index} isLoading={studiosLoading} studio={{}} fromSearch />
           ))
         ) : error ? (
           <SearchError 
             error={error} 
             onRetry={() => window.location.reload()} 
           />
         ) : allStudios && allStudios?.length === 0 && !studiosLoading && !isInitializing ? (
           <div className={classes.noResultsContainer}>
             <h2 className="titleSemibold">
              {params.get('activity') || params.get('location') 
                ? `Keine Ergebnisse für "${params.get('activity') || params.get('location')}"` 
                : 'Keine Ergebnisse'
              }
            </h2>
             <p className="mediumSmall">Hilf uns, besser zu werden!</p>
             <Link to="/kontakt" className={classes.helpUsGetBetterButton}>
               <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                 <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" fill="white"/>
                 <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" fill="white"/>
                 <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                 <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
               </svg>
               <span className="mediumLarge">
                 Suchanfrage vorschlagen
               </span>
             </Link>
           </div>
         ) : (
          <CarouselSearchPriorityProvider>
           {displayedStudios?.map((studio) => (
             <StudioProfile key={studio.id} studio={studio} isLoading={false} fromSearch />
           ))}
          </CarouselSearchPriorityProvider>
         )}
       </div>
      )}
      {!studiosLoading && (
        <SearchFilters
          open={showFilters}
          onClose={() => setShowFilters(false)}
          urlState={urlState}
          onFilterUpdate={handleFilterUpdate}
          onReset={resetFilters}
          allStudios={unfilteredStudios}
        />
      )}
      {!studiosLoading && filteredStudios.length > 0 && !urlShowMap && (
        <div className={classes.paginationContainer}>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
        </div>
      )}
      
      {/* Special container for users without studios */}
      {/* {isLimitedUser && displayedStudios.length > 0 && !studiosLoading && (
        <div className={`${classes.accessAllStudiosContainer} ${isLimitedUser ? classes.noMargin : ''}`}>
          <div className={classes.accessAllStudiosContent}>
            <h3 className="titleSemibold">Zugang zu allen Studios</h3>
            <p className="mediumLarge">
              Werde Gastgeber und siehe alle Studios.
            </p>
            <button onClick={handleGastgeberClick} className={classes.createStudioButton}>
              <span className="regularLarge">Gastgeber werden</span>
            </button>
          </div>
        </div>
      )} */}
      
        {(displayedStudios.length > 0 && !studiosLoading && !isLimitedUser) &&
          <button
             className={`${urlShowMap ? classes.mapClose : classes.mapOpen} regularSmall `}
             onClick={handleMapButtonClick}
             style={{ 
              bottom: !urlShowMap ? `${mapButtonPosition}px` : '80px' 
            }}
          >
             {urlShowMap ? 'Liste anzeigen' : 'Karte anzeigen'}
         </button>
        }
      </div>
      <div className={classes.footerContainer}>
        <Footer />
        <BottomFooter />
      </div>
    </div>
  );
};

export default SearchPage;
